import React from "react";
import { Checkbox } from "@mui/material";
import { inlineChoiceControlSx } from "../../../common/dashboard/formStyles";
import type { EventAttendeeChoiceFields } from "../eventBackendMappers";
import EventAttendeeDetailFields from "./EventAttendeeDetailFields";
import {
  applyEventAttendeeSuggestion,
  type EventAttendeeSuggestion,
} from "./eventAttendeeSuggestions";
import {
  getEventInvoiceAttendeeFieldErrorKey,
  type EventInvoiceAttendee,
  type EventInvoiceAttendeeRequiredField,
} from "./eventInvoiceCreateState";

type EventInvoiceAttendeeFieldsProps = Readonly<{
  attendees: EventInvoiceAttendee[];
  choiceFields: EventAttendeeChoiceFields;
  disabled?: boolean;
  errorKeyPrefix: string;
  errors: Readonly<Record<string, string>>;
  /** Section helper under the header (Create Sponsorship Invoice). */
  helperText?: string;
  onChange: (attendees: EventInvoiceAttendee[]) => void;
  onClearError: (errorKey: string) => void;
  /**
   * Attendee 1 of the whole invoice: the only attendee whose name and email
   * carry a star, whose email is required, and who has no "details later"
   * choice.
   */
  primaryAttendeeId?: string;
  suggestions?: readonly EventAttendeeSuggestion[];
  /** Member company names for the Attendee Company list (design "MEMBERS"). */
  companyOptions?: readonly string[];
  /** Ticket type name, or "{Level} Tickets" for a sponsorship. */
  title: string;
}>;

// Claude Design (8) CMP Dashboard.dc.html:3204-3284 (Create Ticket Invoice)
// and :4865-4941 (Create Sponsorship Invoice): one section per ticket type,
// one card per attendee seat.
export const eventInvoiceDetailsLaterLabel =
  "If you don’t know the additional attendee(s), check the box and provide the details later.";

export const getEventInvoiceTicketCountLabel = (count: number) =>
  `${count} ${count === 1 ? "ticket" : "tickets"}`;

const EventInvoiceAttendeeFields = ({
  attendees,
  choiceFields,
  disabled = false,
  errorKeyPrefix,
  errors,
  helperText,
  onChange,
  onClearError,
  primaryAttendeeId,
  suggestions = [],
  companyOptions = [],
  title,
}: EventInvoiceAttendeeFieldsProps): React.ReactElement => {
  const getFieldError = (
    attendeeId: string,
    field: EventInvoiceAttendeeRequiredField,
  ) =>
    errors[
      getEventInvoiceAttendeeFieldErrorKey({
        attendeeId,
        errorKeyPrefix,
        field,
      })
    ];

  const clearFieldError = (
    attendeeId: string,
    field: EventInvoiceAttendeeRequiredField,
  ) =>
    onClearError(
      getEventInvoiceAttendeeFieldErrorKey({
        attendeeId,
        errorKeyPrefix,
        field,
      }),
    );

  const updateAttendee = (
    attendeeId: string,
    patch: Partial<EventInvoiceAttendee>,
  ) =>
    onChange(
      attendees.map((attendee) =>
        attendee.id === attendeeId ? { ...attendee, ...patch } : attendee,
      ),
    );


  return (
    <section className="mb-5 mt-2 flex flex-col" data-attendee-section={errorKeyPrefix}>
      <div className="flex items-baseline justify-between gap-4 border-b border-[#ECECEC] pb-[14px]">
        <h4 className="m-0 text-[18px] font-bold leading-[normal] text-[#0A0A0A]">
          {title}
        </h4>
        <span className="whitespace-nowrap text-[14px] text-[#0A0A0A]">
          {getEventInvoiceTicketCountLabel(attendees.length)}
        </span>
      </div>
      {helperText ? (
        <p className="m-0 pt-[14px] text-[14px] leading-[1.6] text-[#0A0A0A]">
          {helperText}
        </p>
      ) : null}

      {attendees.map((attendee, index) => {
        const primary = attendee.id === primaryAttendeeId;
        const detailsLaterName = `Attendee ${index + 1}`;
        return (
          <div
            className={`flex flex-col gap-[18px] pb-6 pt-[22px] ${
              index === attendees.length - 1 ? "" : "border-b border-[#ECECEC]"
            }`}
            data-attendee-card={attendee.id}
            key={attendee.id}
          >
            <p className="m-0 text-[13px] font-bold uppercase leading-[normal] tracking-[.06em] text-[#0A0A0A]">
              Ticket {index + 1} of {attendees.length}
            </p>
            {!primary ? (
              <label className="flex items-center gap-[10px] text-[15px] text-[#0A0A0A]">
                <Checkbox
                  sx={inlineChoiceControlSx}
                  checked={Boolean(attendee.detailsLater)}
                  disabled={disabled}
                  onChange={({ target }) => {
                    if (target.checked) {
                      updateAttendee(attendee.id, {
                        detailsLater: true,
                        name: detailsLaterName,
                        nameBeforeDetailsLater: attendee.name,
                      });
                      clearFieldError(attendee.id, "name");
                      return;
                    }
                    updateAttendee(attendee.id, {
                      detailsLater: false,
                      name:
                        attendee.name === detailsLaterName
                          ? attendee.nameBeforeDetailsLater || ""
                          : attendee.name,
                      nameBeforeDetailsLater: undefined,
                    });
                  }}
                />
                <span>{eventInvoiceDetailsLaterLabel}</span>
              </label>
            ) : null}
            <EventAttendeeDetailFields
              ariaName={`Attendee ${index + 1}`}
              choiceFields={choiceFields}
              companyOptions={companyOptions}
              disabled={disabled}
              emailError={getFieldError(attendee.id, "email")}
              fieldId={(suffix) => `${errorKeyPrefix}-${attendee.id}-${suffix}`}
              nameError={getFieldError(attendee.id, "name")}
              number={index + 1}
              onChange={(patch) => updateAttendee(attendee.id, patch)}
              onClearError={(field) => clearFieldError(attendee.id, field)}
              onPickSuggestion={(suggestion) =>
                updateAttendee(
                  attendee.id,
                  applyEventAttendeeSuggestion({ attendee, suggestion }),
                )
              }
              primary={primary}
              suggestions={suggestions}
              values={attendee}
            />
          </div>
        );
      })}
    </section>
  );
};

export default EventInvoiceAttendeeFields;
